/**
 * Tracked Bills Hook
 *
 * Hook for managing tracked bills using the shared tracking factory.
 * Extends the base tracking with bill-specific features:
 * - notify_on_change preference
 * - notes field
 * - update mutation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createTrackingHook, createTrackedIdsHook } from './useTracking';
import { POSTGRES_UNIQUE_VIOLATION } from './queryHelpers';
import type { BillId, TrackedBill, TrackBillOptions } from '@/types/v2';

const QUERY_KEY = ['tracked-bills'] as const;

// Configuration for bill tracking
const BILL_TRACKING_CONFIG = {
  tableName: 'user_tracked_bills',
  queryKey: 'tracked-bills',
  entityIdField: 'bill_id',
  tierLimitKey: 'trackedBills' as const,
  selectColumns: 'id, user_id, bill_id, tracked_at, notify_on_change, notes',
  getEntityId: (item: TrackedBill) => item.bill_id,
  entityName: 'bill',
};

// Create the base tracking hook
const useBaseTrackedBills = createTrackingHook<TrackedBill>(BILL_TRACKING_CONFIG);

/**
 * Track a bill with extra options (notify_on_change, notes)
 */
async function trackBillWithOptions(
  userId: string,
  options: TrackBillOptions
): Promise<TrackedBill> {
  const { data, error } = await supabase
    .from('user_tracked_bills')
    .insert({
      user_id: userId,
      bill_id: options.billId,
      notify_on_change: options.notifyOnChange ?? true,
      notes: options.notes ?? null,
    })
    .select()
    .single();

  if (error) {
    if (error.code === POSTGRES_UNIQUE_VIOLATION) {
      throw new Error('You are already tracking this bill');
    }
    throw new Error(`Failed to track bill: ${error.message}`);
  }

  return data;
}

/**
 * Update tracking preferences for a bill
 */
async function updateTrackedBill(
  userId: string,
  billId: BillId,
  updates: { notifyOnChange?: boolean; notes?: string | null }
): Promise<TrackedBill> {
  const { data, error } = await supabase
    .from('user_tracked_bills')
    .update({
      notify_on_change: updates.notifyOnChange,
      notes: updates.notes,
    })
    .eq('user_id', userId)
    .eq('bill_id', billId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update tracked bill: ${error.message}`);
  }

  return data;
}

/**
 * Hook to manage tracked bills for the current user
 *
 * Extends the base tracking hook with bill-specific features:
 * - Track with notify_on_change and notes options
 * - Update tracked bill preferences
 *
 * @returns Object with tracked bills data and mutation functions
 *
 * @example
 * const {
 *   trackedBills,
 *   trackedBillIds,
 *   isTracked,
 *   trackBill,
 *   untrackBill,
 *   isLoading
 * } = useTrackedBills();
 *
 * // Check if a bill is tracked
 * if (isTracked(123)) { ... }
 *
 * // Track a bill with options
 * await trackBill.mutateAsync({ billId: 123, notifyOnChange: true });
 *
 * // Untrack a bill
 * await untrackBill.mutateAsync(123);
 */
export function useTrackedBills() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;
  const base = useBaseTrackedBills();

  // Extended track mutation with options support
  const trackMutation = useMutation({
    mutationFn: (options: TrackBillOptions) => trackBillWithOptions(userId!, options),
    onMutate: async (options) => {
      await queryClient.cancelQueries({ queryKey: [...QUERY_KEY, userId] });

      const previousTrackedBills = queryClient.getQueryData<TrackedBill[]>([...QUERY_KEY, userId]);

      const optimisticBill: TrackedBill = {
        id: `temp-${options.billId}`,
        user_id: userId!,
        bill_id: options.billId,
        tracked_at: new Date().toISOString(),
        notify_on_change: options.notifyOnChange ?? true,
        notes: options.notes ?? null,
      };

      queryClient.setQueryData<TrackedBill[]>([...QUERY_KEY, userId], (old) => [
        optimisticBill,
        ...(old ?? []),
      ]);

      return { previousTrackedBills };
    },
    onError: (_err, _options, context) => {
      if (context?.previousTrackedBills) {
        queryClient.setQueryData([...QUERY_KEY, userId], context.previousTrackedBills);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, userId] });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({
      billId,
      updates,
    }: {
      billId: BillId;
      updates: { notifyOnChange?: boolean; notes?: string | null };
    }) => updateTrackedBill(userId!, billId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, userId] });
    },
  });

  // Toggle function that uses the extended track mutation
  const toggleTracking = async (billId: BillId): Promise<void> => {
    if (base.isTracked(billId)) {
      await base.untrackMutation.mutateAsync(billId);
    } else {
      await trackMutation.mutateAsync({ billId });
    }
  };

  return {
    // Data
    trackedBills: base.trackedItems,
    trackedBillIds: base.trackedIds,
    trackedCount: base.trackedCount,

    // Tier limits
    billLimit: base.limit,
    canTrackMore: base.canTrackMore,
    remainingSlots: base.remainingSlots,

    // State
    isLoading: base.isLoading,
    isError: base.isError,
    error: base.error,

    // Helpers
    isTracked: base.isTracked,

    // Mutations (bill-specific names, extended track mutation)
    trackBill: trackMutation,
    untrackBill: base.untrackMutation,
    updateTrackedBill: updateMutation,

    // Convenience
    toggleTracking,
    isToggling: trackMutation.isPending || base.untrackMutation.isPending,

    // Refetch
    refetch: base.refetch,
  };
}

// Create base tracked IDs hook
const useBaseTrackedBillIds = createTrackedIdsHook<TrackedBill>(BILL_TRACKING_CONFIG);

/**
 * Hook to get just the tracked bill IDs (lightweight)
 * Useful for components that only need to check if a bill is tracked
 */
export function useTrackedBillIds() {
  const base = useBaseTrackedBillIds();
  return {
    trackedBillIds: base.trackedIds,
    isLoading: base.isLoading,
    isTracked: base.isTracked,
  };
}
